/**
 * Customer Accounts API Client
 */

import {
  getHttpClient,
  isNumericId,
  normalizeEntity,
  normalizeRelation,
  normalizeRelationArray,
  parseNumber,
} from '@spcc/core'
import type {
  CityRef,
  CustomerAccount,
  CustomerAccountStats,
  CustomerAccountsFilter,
  CustomerAccountsPagination,
  PaginatedCustomerAccounts,
  AccountStatus,
  PaymentTerms,
  RegionRef,
  TenantChannel,
  TenantRef,
} from '../../domain/types'

const CUSTOMER_ACCOUNTS_ENDPOINT = '/api/customer-accounts'
const DEFAULT_CURRENCY = 'SAR'

type RecordValue = Record<string, unknown>
type StrapiCollectionResponse<T> = {
  data?: T[]
  meta?: {
    pagination?: {
      page?: number
      pageSize?: number
      pageCount?: number
      total?: number
    }
  }
}
type StrapiSingleResponse<T> = {
  data?: T
}

const PAYMENT_TERMS: PaymentTerms[] = ['cod', 'prepaid', 'net_7', 'net_15', 'net_30', 'net_60', 'net_90', 'custom']
const ACCOUNT_STATUSES: AccountStatus[] = ['active', 'suspended', 'blocked']
const TENANT_CHANNELS: TenantChannel[] = ['b2b', 'ecommerce']

function toStringValue(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return fallback
}

function createIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function toPaymentTerms(value: unknown): PaymentTerms {
  return PAYMENT_TERMS.includes(value as PaymentTerms) ? (value as PaymentTerms) : 'cod'
}

function toAccountStatus(value: unknown): AccountStatus {
  return ACCOUNT_STATUSES.includes(value as AccountStatus) ? (value as AccountStatus) : 'active'
}

function toTenantChannel(value: unknown): TenantChannel {
  return TENANT_CHANNELS.includes(value as TenantChannel) ? (value as TenantChannel) : 'ecommerce'
}

function mapTenantRef(accountEntity: RecordValue): TenantRef {
  const tenantEntity = normalizeRelation<RecordValue>(accountEntity.tenant)
  const fallbackId = toStringValue(accountEntity.tenantId, '')
  const sapBp = toStringValue(tenantEntity?.sapBusinessPartnerNumber, '') || undefined

  return {
    id: toStringValue(tenantEntity?.id, fallbackId),
    documentId: toStringValue(tenantEntity?.documentId, fallbackId),
    code: toStringValue(tenantEntity?.code, 'N/A'),
    name: toStringValue(tenantEntity?.name, 'Unknown Tenant'),
    channel: toTenantChannel(tenantEntity?.channel),
    sapBusinessPartnerNumber: sapBp,
  }
}

function mapRegionRef(entity: RecordValue): RegionRef {
  return {
    id: toStringValue(entity.id, ''),
    documentId: toStringValue(entity.documentId, toStringValue(entity.id, '')),
    code: toStringValue(entity.code, ''),
    name: toStringValue(entity.name, ''),
  }
}

function mapCityRef(entity: RecordValue): CityRef {
  return {
    id: toStringValue(entity.id, ''),
    documentId: toStringValue(entity.documentId, toStringValue(entity.id, '')),
    code: toStringValue(entity.code, ''),
    name: toStringValue(entity.name, ''),
  }
}

function mapEntityToAccount(rawEntity: unknown): CustomerAccount | null {
  const entity = normalizeEntity<RecordValue>(rawEntity)
  if (!entity) return null

  const id = toStringValue(entity.id, '')
  const documentId = toStringValue(entity.documentId, id)
  const tenant = mapTenantRef(entity)

  const allowedRegions = normalizeRelationArray<RecordValue>(entity.allowedRegions).map(mapRegionRef)
  const allowedCities = normalizeRelationArray<RecordValue>(entity.allowedCities).map(mapCityRef)

  return {
    id,
    documentId,
    accountNumber: toStringValue(entity.accountNumber, ''),
    tenant,
    creditLimit: parseNumber(entity.creditLimit, 0),
    paymentTerms: toPaymentTerms(entity.paymentTerms),
    customTermsDays:
      entity.customTermsDays !== undefined && entity.customTermsDays !== null
        ? parseNumber(entity.customTermsDays, 0)
        : undefined,
    accountStatus: toAccountStatus(entity.accountStatus),
    vibanNumber: toStringValue(entity.vibanNumber, '') || undefined,
    lastPaymentDate: toStringValue(entity.lastPaymentDate, '') || undefined,
    notes: toStringValue(entity.notes, '') || undefined,
    metadata: (entity.metadata as Record<string, unknown>) ?? undefined,
    createdAt: toStringValue(entity.createdAt, ''),
    updatedAt: toStringValue(entity.updatedAt, ''),
    allowedRegions: allowedRegions.length > 0 ? allowedRegions : undefined,
    allowedCities: allowedCities.length > 0 ? allowedCities : undefined,
    currentBalance: parseNumber(entity.currentBalance, 0),
    availableCredit: parseNumber(entity.availableCredit, 0),
    overdueAmount: parseNumber(entity.overdueAmount, 0),
  }
}

function applyPostFetchFilter(
  accounts: CustomerAccount[],
  filter?: CustomerAccountsFilter
): CustomerAccount[] {
  if (!filter?.hasOverdue) return accounts
  return accounts.filter((account) => (account.overdueAmount ?? 0) > 0)
}

function toPositiveNumber(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10)
    if (Number.isFinite(parsed) && parsed > 0) return parsed
  }
  return fallback
}

function parsePagination(
  response: StrapiCollectionResponse<unknown>,
  fallbackPage: number,
  fallbackPageSize: number
): CustomerAccountsPagination {
  const metaPagination = response.meta?.pagination
  const page = toPositiveNumber(metaPagination?.page, fallbackPage)
  const pageSize = toPositiveNumber(metaPagination?.pageSize, fallbackPageSize)
  const total = toPositiveNumber(metaPagination?.total, 0)
  const pageCount = toPositiveNumber(
    metaPagination?.pageCount,
    Math.max(1, Math.ceil(total / Math.max(1, pageSize)))
  )

  return {
    page,
    pageSize,
    pageCount,
    total,
  }
}

function buildListQuery(filter?: CustomerAccountsFilter): string {
  const params = new URLSearchParams()
  const page = filter?.page ?? 1
  const pageSize = filter?.pageSize ?? 10
  params.set('pagination[page]', String(page))
  params.set('pagination[pageSize]', String(pageSize))
  params.set('sort[0]', 'createdAt:desc')
  params.set('filters[deletedAt][$null]', 'true')

  params.set('populate[tenant][fields][0]', 'code')
  params.set('populate[tenant][fields][1]', 'name')
  params.set('populate[tenant][fields][2]', 'documentId')
  params.set('populate[tenant][fields][3]', 'channel')
  params.set('populate[tenant][fields][4]', 'sapBusinessPartnerNumber')
  params.set('populate[allowedRegions][fields][0]', 'code')
  params.set('populate[allowedRegions][fields][1]', 'name')
  params.set('populate[allowedRegions][fields][2]', 'documentId')
  params.set('populate[allowedCities][fields][0]', 'code')
  params.set('populate[allowedCities][fields][1]', 'name')
  params.set('populate[allowedCities][fields][2]', 'documentId')
  params.set('populate[allowedCities][fields][3]', 'isActive')

  if (filter?.status && filter.status !== 'all') {
    params.set('filters[accountStatus][$eq]', filter.status)
  }

  if (filter?.paymentTerms && filter.paymentTerms !== 'all') {
    params.set('filters[paymentTerms][$eq]', filter.paymentTerms)
  }

  if (filter?.channel && filter.channel !== 'all') {
    params.set('filters[tenant][channel][$eq]', filter.channel)
  }

  if (filter?.tenantId && filter.tenantId !== 'all') {
    if (isNumericId(filter.tenantId)) {
      params.set('filters[tenant][id][$eq]', filter.tenantId)
    } else {
      params.set('filters[tenant][documentId][$eq]', filter.tenantId)
    }
  }

  if (filter?.search) {
    params.set('filters[$or][0][accountNumber][$containsi]', filter.search)
    params.set('filters[$or][1][vibanNumber][$containsi]', filter.search)
    params.set('filters[$or][2][tenant][name][$containsi]', filter.search)
    params.set('filters[$or][3][tenant][code][$containsi]', filter.search)
    params.set('filters[$or][4][tenant][sapBusinessPartnerNumber][$containsi]', filter.search)
  }

  return `?${params.toString()}`
}

function buildDetailQuery(): string {
  const params = new URLSearchParams()
  params.set('populate[tenant][fields][0]', 'code')
  params.set('populate[tenant][fields][1]', 'name')
  params.set('populate[tenant][fields][2]', 'documentId')
  params.set('populate[tenant][fields][3]', 'channel')
  params.set('populate[tenant][fields][4]', 'sapBusinessPartnerNumber')
  params.set('populate[allowedRegions][fields][0]', 'code')
  params.set('populate[allowedRegions][fields][1]', 'name')
  params.set('populate[allowedRegions][fields][2]', 'documentId')
  params.set('populate[allowedCities][fields][0]', 'code')
  params.set('populate[allowedCities][fields][1]', 'name')
  params.set('populate[allowedCities][fields][2]', 'documentId')
  params.set('populate[allowedCities][fields][3]', 'isActive')
  return `?${params.toString()}`
}

async function resolveDocumentId(accountIdOrDocumentId: string): Promise<string> {
  if (!accountIdOrDocumentId) throw new Error('Account identifier is required')
  if (!isNumericId(accountIdOrDocumentId)) return accountIdOrDocumentId

  const http = getHttpClient()
  const params = new URLSearchParams()
  params.set('filters[id][$eq]', accountIdOrDocumentId)
  params.set('fields[0]', 'documentId')
  params.set('pagination[page]', '1')
  params.set('pagination[pageSize]', '1')

  const response = await http.get<StrapiCollectionResponse<unknown>>(
    `${CUSTOMER_ACCOUNTS_ENDPOINT}?${params.toString()}`
  )

  const data = Array.isArray(response.data?.data) ? response.data.data : []
  const entity = normalizeEntity<RecordValue>(data[0])
  const documentId = toStringValue(entity?.documentId, '')
  if (!documentId) throw new Error('Account not found')
  return documentId
}

async function fetchAccountByDocumentId(documentId: string): Promise<CustomerAccount | null> {
  const http = getHttpClient()
  try {
    const response = await http.get<StrapiSingleResponse<unknown>>(
      `${CUSTOMER_ACCOUNTS_ENDPOINT}/${documentId}${buildDetailQuery()}`
    )
    return mapEntityToAccount(response.data?.data)
  } catch (error) {
    const axiosError = error as { response?: { status?: number } }
    if (axiosError.response?.status === 404) return null
    throw error
  }
}

function calculateStats(accounts: CustomerAccount[]): CustomerAccountStats {
  return {
    total: accounts.length,
    active: accounts.filter((account) => account.accountStatus === 'active').length,
    suspended: accounts.filter((account) => account.accountStatus === 'suspended').length,
    blocked: accounts.filter((account) => account.accountStatus === 'blocked').length,
    totalCreditLimit: {
      amount: accounts.reduce((sum, account) => sum + account.creditLimit, 0),
      currency: DEFAULT_CURRENCY,
    },
    totalOutstanding: {
      amount: accounts.reduce((sum, account) => sum + (account.currentBalance ?? 0), 0),
      currency: DEFAULT_CURRENCY,
    },
    totalOverdue: {
      amount: accounts.reduce((sum, account) => sum + (account.overdueAmount ?? 0), 0),
      currency: DEFAULT_CURRENCY,
    },
  }
}

export const customerAccountsApi = {
  async getAccounts(filter?: CustomerAccountsFilter): Promise<PaginatedCustomerAccounts> {
    const http = getHttpClient()
    const requestedPage = filter?.page ?? 1
    const requestedPageSize = filter?.pageSize ?? 10
    const response = await http.get<StrapiCollectionResponse<unknown>>(
      `${CUSTOMER_ACCOUNTS_ENDPOINT}${buildListQuery(filter)}`
    )

    const rawList = Array.isArray(response.data?.data) ? response.data.data : []
    const mapped = rawList
      .map(mapEntityToAccount)
      .filter((account): account is CustomerAccount => account !== null)
    const filtered = applyPostFetchFilter(mapped, filter)

    return {
      data: filtered,
      pagination: parsePagination(response.data ?? {}, requestedPage, requestedPageSize),
    }
  },

  async getStats(): Promise<CustomerAccountStats> {
    const firstPage = await customerAccountsApi.getAccounts({ page: 1, pageSize: 200 })
    const allAccounts: CustomerAccount[] = [...firstPage.data]

    if (firstPage.pagination.pageCount > 1) {
      for (let page = 2; page <= firstPage.pagination.pageCount; page += 1) {
        const nextPage = await customerAccountsApi.getAccounts({ page, pageSize: 200 })
        allAccounts.push(...nextPage.data)
      }
    }

    return calculateStats(allAccounts)
  },

  async getAccount(idOrDocumentId: string): Promise<CustomerAccount | null> {
    const documentId = await resolveDocumentId(idOrDocumentId)
    return fetchAccountByDocumentId(documentId)
  },

  async updateAccount(
    accountId: string,
    data: Partial<CustomerAccount>
  ): Promise<CustomerAccount> {
    const http = getHttpClient()
    const documentId = await resolveDocumentId(accountId)

    const payload: Record<string, unknown> = {}
    if (data.creditLimit !== undefined) payload.creditLimit = Number(data.creditLimit)
    if (data.paymentTerms !== undefined) payload.paymentTerms = data.paymentTerms
    if (data.customTermsDays !== undefined) payload.customTermsDays = data.customTermsDays
    if (data.notes !== undefined) payload.notes = data.notes

    await http.put(
      `${CUSTOMER_ACCOUNTS_ENDPOINT}/${documentId}`,
      { data: payload },
      { headers: { 'Idempotency-Key': createIdempotencyKey() } }
    )

    const updated = await customerAccountsApi.getAccount(documentId)
    if (!updated) {
      throw new Error('Account not found after update')
    }
    return updated
  },

  async changeStatus(accountId: string, status: AccountStatus): Promise<CustomerAccount> {
    const http = getHttpClient()
    const documentId = await resolveDocumentId(accountId)

    await http.put(
      `${CUSTOMER_ACCOUNTS_ENDPOINT}/${documentId}`,
      { data: { accountStatus: status } },
      { headers: { 'Idempotency-Key': createIdempotencyKey() } }
    )

    const updated = await customerAccountsApi.getAccount(documentId)
    if (!updated) {
      throw new Error('Account not found after status change')
    }
    return updated
  },

  async updateAllowedCities(accountId: string, cityIds: string[]): Promise<CustomerAccount> {
    const http = getHttpClient()
    const documentId = await resolveDocumentId(accountId)

    const normalizedCityIds = cityIds.map((cityId) => (isNumericId(cityId) ? Number(cityId) : cityId))

    await http.put(
      `${CUSTOMER_ACCOUNTS_ENDPOINT}/${documentId}`,
      {
        data: {
          allowedCities: normalizedCityIds,
        },
      },
      {
        headers: {
          'Idempotency-Key': createIdempotencyKey(),
        },
      }
    )

    const updated = await customerAccountsApi.getAccount(documentId)
    if (!updated) {
      throw new Error('Account not found after allowed cities update')
    }
    return updated
  },
}
